import React, { createContext, useContext, useEffect, useState } from 'react';
import { Meal } from '../../types/Meal.type';
import { CartContextType, CartProviderProps, MealCart } from './CartContext.types';
import { useSduiService } from '../../api/rest/hooks/useSduiService';

const CartLocalStorageKey = 'aw-cart';
const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<CartProviderProps> = ({ children, deliveryDate, cartUpdated }) => {
    const { addMeal, removeMeal } = useSduiService();
    const [cart, setCart] = useState<MealCart[]>(() => {
        const storedCart = localStorage.getItem(`${CartLocalStorageKey}-${deliveryDate}`);
        return storedCart ? JSON.parse(storedCart) ?? [] : [];
    });

    useEffect(() => {
        localStorage.setItem(`${CartLocalStorageKey}-${deliveryDate}`, JSON.stringify(cart));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart]);

    const triggerCartUpdated = () => {
        if (typeof cartUpdated === 'function') {
            cartUpdated();
        }
    }

    const addMealToCart = async (meal: Meal | MealCart) => {
        setCart((prevCart) => {
            const existingMealIndex = prevCart.findIndex((m) => m.id === meal.id);
            if (existingMealIndex !== -1) {
                const updatedCart = prevCart.map((m, index) =>
                    index === existingMealIndex
                        ? { ...m, quantity: m.quantity + 1 }
                        : m
                );
                return updatedCart;
            } else {
                const { id, batchId, inventoryId, name, image } = meal;
                return [...prevCart, { id, batchId, inventoryId, image, name, price: 0, quantity: 1 }];
            }
        });

        try {
            await addMeal(deliveryDate as string, meal as MealCart);
            triggerCartUpdated();
        } catch (error) {
            console.error(error);  
        }

    };

    const updateMealQuantity = (mealId: number, quantity: number) => {
        setCart((prevCart) =>
            prevCart.map((m) =>
                m.id === mealId ? { ...m, quantity: quantity } : m
            )
        );
    };

    const removeMealFromCart = async (meal: Meal | MealCart) => {
        setCart((prevCart) => {
            return prevCart
                .map((m) => {
                    if (m.id === meal.id) {
                        const updatedQuantity = m.quantity - 1;
                        return updatedQuantity > 0 ? { ...m, quantity: updatedQuantity } : null;
                    }
                    return m;
                })
                .filter((m): m is MealCart => m !== null);
        });

        
        try {
            await removeMeal(deliveryDate as string, meal as MealCart);
            triggerCartUpdated();
        } catch (error) {
            console.error(error);
        }
    };

    const eraseCart = () => {
        setCart([]);
    }

    const getMealById = (mealId: number): MealCart | undefined => {
        return cart.find((m) => m.id === mealId);
    };

    const getCartCount = () => {
        return cart.reduce((acc, meal) => acc + meal.quantity, 0);
    }

    return (
        <CartContext.Provider value={{ cart, addMealToCart, updateMealQuantity, removeMealFromCart, getMealById, getCartCount, eraseCart }}>
            {children}
        </CartContext.Provider>
    );
};


export const useCart = (): CartContextType => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};